/**
 * Landing page — first screen for both returning clients and future clients
 * onboarded by NZ Taxation Ltd. Deliberately NOT a marketing site: it's a
 * living preview of what the client's own portal will feel like.
 *
 * Design north-star: answers the four doorway questions before you even sign
 * in ("Can I afford this?", "Who owes me money?", "What should I do today?",
 * "Where's my next opportunity?"). The dimmed rail at the bottom holds space
 * for the six-score family that arrives in Year 3.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, AlertCircle, CheckCircle2, Lock, Flame, Sparkles } from "lucide-react";

// A number that gently counts up on mount so the preview feels alive without
// being casino-y. 900ms, ease-out, plays once.
function useCountUp(target, durationMs = 900) {
    const [n, setN] = useState(0);
    useEffect(() => {
        let raf;
        const start = performance.now();
        const step = (t) => {
            const p = Math.min(1, (t - start) / durationMs);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.round(target * eased));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [target, durationMs]);
    return n;
}

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });

export default function Landing() {
    // The five locked-score cells at the bottom. Kept as a config so future
    // sprints just flip `live: true` when each score ships.
    const scoreRail = [
        { key: "health",    label: "Business Health", value: 87, live: true },
        { key: "profit",    label: "Profit",          value: null, live: false },
        { key: "savings",   label: "Savings",         value: null, live: false },
        { key: "referral",  label: "Mates joined",    value: null, live: false },
        { key: "education", label: "Learning",        value: null, live: false },
        { key: "streak",    label: "Streak",          value: null, live: false, streak: true },
    ];

    return (
        <div
            className="min-h-screen relative overflow-hidden bg-[#0B1F17] text-[#F0EDE5] antialiased"
            data-testid="landing-page"
        >
            {/* Ambient warm glow — subtle radial to add depth to the dark canvas */}
            <div className="pointer-events-none absolute inset-0 opacity-70">
                <div className="absolute -top-40 -left-32 h-[520px] w-[520px] rounded-full bg-[#0E3B2E] blur-3xl opacity-60" />
                <div className="absolute top-[10%] right-[-10%] h-[420px] w-[420px] rounded-full bg-[#B45309]/25 blur-3xl" />
                <div className="absolute bottom-[-15%] left-[20%] h-[440px] w-[440px] rounded-full bg-[#0E3B2E]/60 blur-3xl" />
            </div>

            {/* Fine grain overlay — kills the "AI slop gradient" look */}
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                }}
            />

            {/* Header: NZ Taxation wordmark + tiny ClientOps tag */}
            <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 pt-6 sm:pt-8" data-testid="landing-header">
                <div className="inline-flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-md bg-[#F0EDE5] text-[#0E3B2E] font-display text-[13px] font-bold">NZ</span>
                    <div className="leading-tight">
                        <div className="text-[13px] font-medium tracking-tight">NZ Taxation Ltd</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#F0EDE5]/50">ClientOps · Aotearoa</div>
                    </div>
                </div>
                <Link
                    to="/login"
                    data-testid="landing-signin-corner"
                    className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[#F0EDE5]/20 bg-[#0B1F17]/40 px-4 py-2 text-[13px] font-medium backdrop-blur-md hover:bg-[#F0EDE5]/10 transition-colors"
                >
                    Sign in <ArrowRight className="h-3.5 w-3.5" />
                </Link>
            </header>

            <main className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 pt-12 sm:pt-20 pb-24">
                {/* Wordmark row */}
                <div className="max-w-2xl">
                    <div className="text-[11px] uppercase tracking-[0.24em] text-[#F0EDE5]/50 mb-4">Your business, in one glance</div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-semibold">
                        A quieter way to run<br />the whole thing.
                    </h1>
                    <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-[#F0EDE5]/70">
                        Can I afford this? Who owes me? What should I do today?
                        Where&apos;s the next opportunity? Sign in and see.
                    </p>
                </div>

                {/* Preview dashboard + sign-in split */}
                <div className="mt-12 lg:mt-16 grid gap-8 lg:grid-cols-[1.55fr_1fr] items-start">
                    <PreviewDashboard />
                    <SignInCard />
                </div>

                {/* Future scores rail — plants the seed for gamification */}
                <div className="mt-14 lg:mt-20">
                    <div className="flex items-baseline justify-between mb-5">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.24em] text-[#F0EDE5]/50">How you&apos;ll be tracking</div>
                            <div className="mt-1 text-sm text-[#F0EDE5]/80">One score today. Five more, quietly, over time.</div>
                        </div>
                        <div className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-[#F0EDE5]/50">
                            <Sparkles className="h-3.5 w-3.5" /> Slow but sure
                        </div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                        {scoreRail.map((s) => (
                            <ScoreCell key={s.key} {...s} />
                        ))}
                    </div>
                </div>

                {/* Fine print */}
                <p className="mt-16 text-[11px] text-[#F0EDE5]/40 leading-relaxed max-w-xl">
                    ClientOps is built and operated by NZ Taxation Ltd for its current and future clients.
                    We&apos;re a remote accounting team — no offices to visit, everything runs through here.
                </p>
            </main>
        </div>
    );
}

/* -----------------------------------------------------------------------
 * Preview dashboard — a redacted "here's what your portal looks like" pane.
 * All numbers are illustrative ("Sample business") so we never mislead a
 * first-time visitor into thinking it's their real data.
 * --------------------------------------------------------------------- */
function PreviewDashboard() {
    const safeToSpend = useCountUp(12340);
    const owedTotal = useCountUp(4220);
    const health = useCountUp(87);

    return (
        <div className="relative" data-testid="landing-preview">
            {/* Frosted glass frame */}
            <div className="absolute inset-0 rounded-[22px] bg-[#F0EDE5]/[0.06] backdrop-blur-md border border-[#F0EDE5]/10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)]" />
            <div className="relative rounded-[22px] p-5 sm:p-7">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#F0EDE5]/40">Sample business · demo view</div>
                        <div className="mt-1 font-display text-lg text-[#F0EDE5]">Kia ora, Harbour Joinery.</div>
                    </div>
                    <span className="hidden sm:inline-flex text-[10px] uppercase tracking-[0.18em] text-[#F0EDE5]/40 border border-[#F0EDE5]/15 rounded-full px-2.5 py-1">Today</span>
                </div>

                {/* Row 1: two hero cards side by side */}
                <div className="mt-5 grid sm:grid-cols-2 gap-3">
                    {/* Safe to Spend */}
                    <div className="rounded-2xl bg-[#F5F1E6] text-[#0B1F17] p-4 relative overflow-hidden">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#0B1F17]/60">Can I afford this?</div>
                        <div className="mt-1 text-[13px] font-medium">Safe to spend today</div>
                        <div className="mt-3 font-display text-3xl tabular-nums leading-none">
                            {NZD.format(safeToSpend)}
                        </div>
                        <div className="mt-2 text-[11px] text-[#0B1F17]/60">After GST set-aside &amp; bills due this week.</div>
                        <span className="absolute right-3 top-3 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-800 bg-emerald-100 rounded-full px-2 py-0.5">
                            <TrendingUp className="h-2.5 w-2.5" /> Healthy
                        </span>
                    </div>
                    {/* Owed to you */}
                    <div className="rounded-2xl bg-[#3A1E15] text-[#F0EDE5] p-4 relative overflow-hidden border border-[#F0EDE5]/5">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#F0EDE5]/55">Who owes me?</div>
                        <div className="mt-1 text-[13px] font-medium">3 invoices overdue</div>
                        <div className="mt-3 font-display text-3xl tabular-nums leading-none">
                            {NZD.format(owedTotal)}
                        </div>
                        <div className="mt-2 text-[11px] text-[#F0EDE5]/55">Oldest is 34 days old — Bay Joinery Ltd.</div>
                        <span className="absolute right-3 top-3 inline-flex items-center gap-1 text-[10px] font-medium text-amber-100 bg-[#B45309] rounded-full px-2 py-0.5">
                            <AlertCircle className="h-2.5 w-2.5" /> Chase
                        </span>
                    </div>
                </div>

                {/* Row 2: today priority + health dial */}
                <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-3 items-stretch">
                    <div className="rounded-2xl bg-[#0F2A21] p-4 border border-[#F0EDE5]/5">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#F0EDE5]/45">What should I do today?</div>
                        <div className="mt-1 flex items-start gap-3">
                            <div className="grid h-8 w-8 place-items-center rounded-full bg-[#F0EDE5] text-[#0B1F17] shrink-0 mt-0.5">
                                <CheckCircle2 className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[15px] font-medium">Sign your GST return by Friday</div>
                                <div className="mt-1 text-[12px] text-[#F0EDE5]/60">$4,120 due 28 July. Money already set aside — one tap to authorise.</div>
                            </div>
                        </div>
                    </div>
                    {/* Health dial */}
                    <div className="rounded-2xl bg-[#0F2A21] p-4 border border-[#F0EDE5]/5 flex sm:flex-col items-center sm:items-start gap-3">
                        <HealthDial value={health} />
                        <div className="sm:mt-1">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#F0EDE5]/45">Business health</div>
                            <div className="text-[12px] text-[#F0EDE5]/70">Strong · trending up</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function HealthDial({ value }) {
    const r = 26;
    const c = 2 * Math.PI * r;
    const off = c - (value / 100) * c;
    return (
        <div className="relative shrink-0" style={{ width: 76, height: 76 }}>
            <svg viewBox="0 0 72 72" className="rotate-[-90deg]" width="76" height="76">
                <circle cx="36" cy="36" r={r} stroke="#F0EDE5" strokeOpacity="0.12" strokeWidth="7" fill="none" />
                <circle
                    cx="36" cy="36" r={r} stroke="#7CBFA1" strokeWidth="7" fill="none" strokeLinecap="round"
                    strokeDasharray={c} strokeDashoffset={off}
                    style={{ transition: "stroke-dashoffset 900ms ease-out" }}
                />
            </svg>
            <div className="absolute inset-0 grid place-items-center">
                <div className="font-display text-lg text-[#F0EDE5] tabular-nums">{value}</div>
            </div>
        </div>
    );
}

/* -----------------------------------------------------------------------
 * Sign-in card — deliberately quiet. It's not a marketing CTA; it's a door.
 * --------------------------------------------------------------------- */
function SignInCard() {
    return (
        <div className="relative" data-testid="landing-signin-card">
            <div className="absolute inset-0 rounded-[22px] bg-[#F0EDE5]/[0.06] backdrop-blur-md border border-[#F0EDE5]/10" />
            <div className="relative rounded-[22px] p-6 sm:p-7">
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#F0EDE5]/50">Welcome</div>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl leading-tight tracking-tight">
                    Let&apos;s pick up<br />where you left off.
                </h2>
                <p className="mt-3 text-[13px] leading-relaxed text-[#F0EDE5]/65">
                    Sign in with your email. We&apos;ll figure out the rest — same login for phone, tablet, and desktop.
                </p>

                <Link
                    to="/login"
                    data-testid="landing-signin"
                    className="group mt-6 inline-flex w-full items-center justify-between rounded-xl bg-[#F0EDE5] px-5 py-3.5 text-[#0B1F17] transition-all hover:bg-white"
                >
                    <span className="font-medium text-[15px]">Sign in</span>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>

                <div className="mt-6 rounded-xl bg-[#0B1F17]/50 border border-[#F0EDE5]/10 p-3.5">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#F0EDE5]/50">First time here?</div>
                    <p className="mt-1.5 text-[13px] text-[#F0EDE5]/80 leading-relaxed">
                        Bring your own Xero or Prosaic. Two minutes to your first snapshot.
                    </p>
                    <Link
                        to="/signup"
                        data-testid="landing-start-free"
                        className="group mt-3 inline-flex w-full items-center justify-between rounded-lg border border-[#F0EDE5]/20 bg-transparent px-4 py-2.5 text-[13px] text-[#F0EDE5] transition-all hover:bg-[#F0EDE5]/10"
                    >
                        <span className="font-medium">Start free</span>
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                </div>
            </div>
        </div>
    );
}

/* -----------------------------------------------------------------------
 * Score rail cell — one lives ("live: true"), five are placeholders. Locked
 * cells still count toward the visual grammar so users see what's coming.
 * --------------------------------------------------------------------- */
function ScoreCell({ label, value, live, streak }) {
    const displayed = useCountUp(value ?? 0, 700);
    return (
        <div
            className={
                "rounded-2xl border p-4 min-h-[110px] flex flex-col justify-between " +
                (live
                    ? "bg-[#F0EDE5]/[0.06] border-[#F0EDE5]/15 backdrop-blur-md"
                    : "bg-[#0B1F17]/40 border-[#F0EDE5]/8 border-dashed opacity-75")
            }
            data-testid={`score-cell-${label.toLowerCase().replaceAll(" ", "-")}`}
        >
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#F0EDE5]/55 truncate">{label}</div>
                {!live && <Lock className="h-3 w-3 text-[#F0EDE5]/30" />}
                {live && streak && <Flame className="h-3.5 w-3.5 text-[#F59E0B]" />}
            </div>
            <div>
                {live ? (
                    <div className="font-display text-2xl tabular-nums leading-none">
                        {streak ? `${displayed}d` : displayed}
                    </div>
                ) : (
                    <div className="font-display text-2xl text-[#F0EDE5]/25 leading-none tabular-nums">—</div>
                )}
                <div className="mt-1 text-[10px] text-[#F0EDE5]/40">
                    {live ? "Live now" : "Coming soon"}
                </div>
            </div>
        </div>
    );
}
